import { rgba } from 'polished';
import { useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { PencilIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Avatar, Button, IconColor, Paragraph, Popover, TextArea, tokens } from '@treams/treams-ui';

const Container = styled.div`
	min-height: 40px;
	overflow: visible;
	position: relative;
`;

const PersonCard = styled(Flex)<{ dragging?: boolean; placed?: boolean; small?: boolean }>`
	padding: 5px;
	box-sizing: border-box;
	width: 154px;
	border-radius: 6px;
	flex-shrink: 0;
	flex-grow: 0;
	margin-right: 10px;
	min-height: 60px;

	${(props) => !props.placed && `background: ${props.theme.colors.lightBlueLight};`}

	${(props) =>
		props.dragging &&
		`
		background: white;
		box-shadow: 0px 8px 16px ${rgba(tokens.border.subtle, 0.25)};

		&,
		* {
			transition: 0.4s ease-out all;
		}
	`}

	${(props) =>
		props.small &&
		`
		padding: 5px;
		min-height: 40px;
	`}
`;

const PersonName = styled(Box)`
	color: ${tokens.text.secondary};
	flex-grow: 1;
	flex-shrink: 1;
`;

const Pencil = styled(Box)`
	svg {
		display: block;
	}
`;

function TalentReviewMotivation(props: {
	motivation: string;
	onChange: (value: string) => void;
	onCancel: () => void;
	readonly?: boolean;
}) {
	const { t } = useTranslation();

	return (
		<Box p={3} width={300}>
			{props.readonly ? (
				<Box tabIndex={0} ref={(ref) => ref?.focus()} onBlur={() => props.onCancel()}>
					<Paragraph>{props.motivation}</Paragraph>
				</Box>
			) : (
				<>
					<TextArea
						minRows={0}
						maxRows={5}
						placeholder={t('talentReviews.edit.motivationPlaceholder')}
						value={props.motivation}
						onChange={props.onChange}
						onBlur={props.onCancel}
						autoFocus
					/>
					<Flex justifyContent="right" mt={3}>
						<Button
							onClick={props.onCancel}
							title={t('talentReviews.edit.motivationDoneButton.title')}
							small
						/>
					</Flex>
				</>
			)}
		</Box>
	);
}

export default function TalentReviewPerson(props: {
	user: graphql.User;
	index: number;
	small?: boolean;
	placed?: boolean;
	motivation?: string;
	onUpdateMotivation?: (motivation: string) => void;
	readonly?: boolean;
}) {
	const [popover, setPopover] = useState(false);

	return (
		<Draggable draggableId={props.user.id} index={props.index} isDragDisabled={props.readonly}>
			{(provided, snapshot) => {
				const isDropping = snapshot.isDropAnimating && snapshot.draggingOver?.includes('cell-');

				return (
					<Container ref={provided.innerRef} {...provided.draggableProps}>
						<Popover.Target
							contents={
								<TalentReviewMotivation
									motivation={props.motivation ?? ''}
									onChange={(motivation) => props.onUpdateMotivation?.(motivation)}
									onCancel={() => setPopover(false)}
									readonly={props.readonly}
								/>
							}
							visible={
								popover &&
								!snapshot.isDragging &&
								(!props.readonly || (props.motivation ?? '') !== '')
							}
							onDismiss={() => setPopover(false)}
						>
							<PersonCard
								placed={props.placed}
								dragging={snapshot.isDragging}
								small={(props.small && !snapshot.isDragging) || isDropping}
								alignItems="center"
								onMouseUp={() => props.placed && setPopover(true)}
								{...provided.dragHandleProps}
							>
								<Box
									mr={(props.small && !snapshot.isDragging) || isDropping ? 1 : 2}
									flexGrow={0}
									flexShrink={0}
								>
									<Avatar
										size={(props.small && !snapshot.isDragging) || isDropping ? 24 : 32}
										user={props.user}
									/>
								</Box>
								<PersonName>
									{props.user.firstName} {props.user.lastName}
								</PersonName>
								{props.placed && !snapshot.isDragging && (!props.readonly || props.motivation) ? (
									<Pencil ml={2} flexShrink={0} flexGrow={0}>
										<Box size={16} flexShrink={0} flexGrow={0}>
											<IconColor color={props.motivation ? 'slateBlue' : 'lightBlueDark'}>
												<PencilIcon />
											</IconColor>
										</Box>
									</Pencil>
								) : (
									<Box ml={2} width={14} />
								)}
							</PersonCard>
						</Popover.Target>
					</Container>
				);
			}}
		</Draggable>
	);
}
